/**
 * Hook exposing whether an order may access the Medical Necessity flow.
 *
 * Loads the live case-gate config once
 * (`configurations/nccn_guidelines/case_gates/live`) and caches it at module
 * scope so every order on the dashboard reuses a single Firestore read. The
 * order's regimen name is fetched from the backend
 * (`POST /backend-service/medical/order/regimen-icd`, PG-primary with a
 * Firestore fallback) so the gate evaluates against the authoritative regimen,
 * then the order's auth status / regimen is checked against the config. The
 * button is hidden until both the config and regimen resolve and stays hidden
 * if the config is missing.
 * @module useNccnCaseGate
 */
import { useEffect, useState } from "react";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../api/firebase/references";
import { getOrderRegimenIcd } from "../../../api/postCall/orderRegimenIcd";
import { NccnCaseGateConfig } from "../../../data-model/nccnCaseGate";
import { logError } from "../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import {
  evaluateMedicalNecessityGate,
  MedicalNecessityGateReason,
} from "../utils/nccnCaseGate";

let cachedConfig: NccnCaseGateConfig | null = null;
let cachedConfigPromise: Promise<NccnCaseGateConfig | null> | null = null;

/** Arguments for {@link useNccnCaseGate}. */
export interface UseNccnCaseGateArgs {
  /**
   * The order's auth status (e.g. `auth_required`, `auth_by_risa`) taken from
   * the auth-on-file object (`authOnFile.auth_status`), NOT the order's
   * `master_auth_status` nor the regimen/ICD API response. This is the value
   * matched against the config's `auth_status` allow-list.
   */
  authStatus?: string | null;
  /** Order ID used to fetch the regimen name from the backend. */
  orderId?: string | null;
}

/** Resolved case-gate state for one order. */
export interface NccnCaseGateState {
  isEligible: boolean;
  isResolved: boolean;
  gateReason: MedicalNecessityGateReason;
  regimenName: string | null;
}

const INITIAL_STATE: NccnCaseGateState = {
  isEligible: false,
  isResolved: false,
  gateReason: null,
  regimenName: null,
};

/**
 * Loads the live case-gate config, memoising the result (and the in-flight
 * request) at module scope. Returns null when the document is missing or the
 * read fails; a failed read clears the cache so a later mount can retry.
 *
 * @returns The case-gate config, or null when unavailable
 */
const loadCaseGateConfig = async (): Promise<NccnCaseGateConfig | null> => {
  if (cachedConfig) return cachedConfig;
  if (!cachedConfigPromise) {
    cachedConfigPromise = FirestoreService.getDocument<NccnCaseGateConfig>(
      FirestoreDocumentReference.nccnCaseGateConfig(),
    )
      .then((config) => {
        cachedConfig = config;
        return config;
      })
      .catch((error) => {
        logError(error as Error, "Error loading NCCN case-gate config");
        cachedConfigPromise = null;
        return null;
      });
  }
  return cachedConfigPromise;
};

/**
 * Fetches the order's regimen name from the backend regimen/ICD endpoint.
 *
 * Only the regimen name is used for the gate; the response's
 * `master_auth_status` is intentionally ignored (auth status comes from the
 * order object instead).
 *
 * @param orderId - The order to fetch the regimen for
 * @returns The regimen name, or null when unavailable or the read fails
 */
const fetchRegimenName = async (orderId: string): Promise<string | null> => {
  try {
    const response = await getOrderRegimenIcd({
      order_id: orderId,
      org_id: getOrgIdForFetchExternalWorklist() || null,
    });
    return response.regimen_name ?? null;
  } catch (error) {
    logError(error as Error, "Error loading order regimen for case gate");
    return null;
  }
};

/**
 * Resolves Medical Necessity eligibility for a single order.
 *
 * @param args - The order's auth status and order ID
 * @returns Eligibility, gate reason, regimen name, and resolved flag
 */
export const useNccnCaseGate = ({
  authStatus,
  orderId,
}: UseNccnCaseGateArgs): NccnCaseGateState => {
  const [state, setState] = useState<NccnCaseGateState>(INITIAL_STATE);

  useEffect(() => {
    let isActive = true;
    setState(INITIAL_STATE);

    if (!orderId) {
      setState({
        ...INITIAL_STATE,
        isResolved: true,
        gateReason: "order_missing",
      });
      return undefined;
    }

    Promise.all([loadCaseGateConfig(), fetchRegimenName(orderId)])
      .then(([config, regimen]) => {
        if (!isActive) return;
        const gate = evaluateMedicalNecessityGate(config, {
          authStatus,
          regimen,
        });
        setState({
          isEligible: gate.isEligible,
          isResolved: true,
          gateReason: gate.gateReason,
          regimenName: regimen,
        });
      })
      .catch((error) => {
        logError(error as Error, "Error resolving NCCN case gate");
        if (!isActive) return;
        setState({
          ...INITIAL_STATE,
          isResolved: true,
          gateReason: "config_unavailable",
        });
      });

    return () => {
      isActive = false;
    };
  }, [authStatus, orderId]);

  return state;
};
